import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ExternalLink,
  Select,
  isLiveOnTwitch,
  teamData
} from '@maxroll-media-group/common-libs'
import { BaseAssetPath, FrontendPath } from 'common'
import React from 'react'
import './content-creator.scss'

export function ContentCreator({
  embed,
  onEmbedChange
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
}) {
  const [isEditing, setIsEditing] = React.useState(false)

  const onOverviewAttrChange = <K extends keyof LevelingOverview>(
    newValue: LevelingOverview[K],
    attr: K
  ) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      return {
        ...embed,
        overview: {
          ...embed.overview,
          [attr]: newValue
        }
      }
    })

  const author = React.useMemo(() => {
    const result =
      teamData.team?.[embed?.overview?.author ?? 'maxroll'] ||
      teamData.team['maxroll']
    return {
      ...result,
      socials: [
        result.social?.twitter
          ? {
              slug: 'twitter',
              link: result.social?.twitter
            }
          : undefined,
        result.social?.twitch
          ? {
              slug: 'twitch',
              link: result.social?.twitch
            }
          : undefined,
        result.social?.youtube
          ? {
              slug: 'youtube',
              link: result.social?.youtube
            }
          : undefined
      ].filter((item) => item)
    }
  }, [embed?.overview?.author])

  const [isLive, setIsLive] = React.useState(false)
  React.useEffect(() => {
    const frontendHost = FrontendPath.split('//')[1]

    setIsLive(false)
    if (author.nickname !== 'Maxroll' && author.social?.twitch) {
      isLiveOnTwitch(
        author.social.twitch.split('twitch.tv/')[1],
        frontendHost
      ).then((result) => {
        setIsLive(result)
      })
    }
  }, [author])

  return (
    <div className="poe-content-creator">
      {isEditing ? (
        <div className="poe-content-creator-edit">
          <Select
            className="poe-commons-lib-select"
            value={embed?.overview?.author || 'maxroll'}
            options={
              teamData.tabs
                .find((item) => item.slug === 'poe')
                ?.members.map((item) => ({
                  value: item.slug,
                  label: item.slug
                })) ?? []
            }
            onChange={(option) => {
              onOverviewAttrChange(option?.value, 'author')
            }}
          />
          <button onClick={() => setIsEditing(false)}>Close</button>
        </div>
      ) : (
        <div className="poe-content-creator-box">
          <div className="poe-image">
            <img
              className={
                author.nickname === 'Maxroll' || author.customImageWidth
                  ? ''
                  : 'poe-zoom-in'
              }
              src={`${BaseAssetPath}/images/${
                author.image || 'maxroll-mark-icon.svg'
              }`}
              alt={author.nickname}
            />
          </div>

          <div className="poe-details">
            {author.socials.length > 0 && (
              <span className="poe-social-icons">
                {author.socials.map((platform, index) => {
                  return platform?.link ? (
                    <ExternalLink
                      key={index}
                      to={platform.link}
                      className="poe-social-icon"
                      style={{
                        maskImage: `url(https://assets-ng.maxroll.gg/images/${platform.slug}-logo-gray.svg?scope=mask)`,
                        WebkitMaskImage: `url(https://assets-ng.maxroll.gg/images/${platform.slug}-logo-gray.svg?scope=mask)`
                      }}></ExternalLink>
                  ) : null
                })}
              </span>
            )}

            <span className="poe-name">
              {onEmbedChange && (
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setIsEditing(!isEditing)
                  }}
                />
              )}

              {author.nickname}
            </span>

            {isLive ? (
              <a className="poe-live-now poe-live-live">
                <span className="poe-live-status"></span>
                Live Now
              </a>
            ) : (
              <span className="poe-live-now poe-offline">Offline</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
